import React, { useState } from 'react';
import { Button } from 'primereact/button';
import { useQuery, useQueryClient } from 'react-query';
import {
    getOrgSubscriptionPlans,
    getOrgSubscriptionInfo,
    getOrgSubscriptionDetails,
    createOrgSubscription,
    changeOrgSubscription,
    transformApiPlansToComponentFormat,
    type SubscriptionPlan,
} from '../../../../helpers/orgSubscriptionHelper';
import { ORG_SUBSCRIPTION_INFO, ORG_SUBSCRIPTION_PLANS_LIST, ORG_SUBSCRIPTION_DETAILS } from '../../../../constants/queryKeys';
import logger from '../../../../utils/logger';
import { useTranslation } from '../../../../hooks/useTranslation';
import { _ToastMessageProps } from '../../../../constants/staticTypes';
import { defaultToastData } from '../../../../helpers/commonHelper';
import MlxToast from '../../../../components/common/MlxToast/MlxToast';
import { AlertVariant } from '../../../../constants/constants';
import {
    CHOOSE_YOUR_PLAN,
    SUBSCRIPTION_PLANS_DESCRIPTION,
    MONTHLY,
    YEARLY,
    FREE,
    PER,
    YEAR,
    MONTH,
    MOST_POPULAR,
    CURRENT_PLAN,
    ACTIVE_PLAN,
    GET_STARTED,
    UPGRADE_PLAN,
    UPCOMING_PLAN,
    LOADING_SUBSCRIPTION_PLANS,
    CONFIRM_YOUR_PLAN,
    START_YOUR_FREE_TRIAL_OF_PLAN,
    ADD_PAYMENT_METHOD_TO_START_SUBSCRIPTION,
    ADD_PAYMENT_METHOD_LINK,
} from '../../../../constants/strings';
import './OrgSubscription.scss';
import MlxBlockUI from '../../../../components/common/MlxBlockUI/MlxBlockUI';
import MlxDialog from '../../../../components/common/MlxDialog/MlxDialog';
import PlanConfirmationModal from './PlanConfirmationModal';
import { useWindowSize } from 'react-use';
import { MOBILE_BREAKPOINT } from '../../../../constants/constants';
import { useNavigate } from 'react-router-dom';
import { routes } from '../../../../routes/allRoutes';

const languageTexts = {
    CHOOSE_YOUR_PLAN,
    SUBSCRIPTION_PLANS_DESCRIPTION,
    MONTHLY,
    YEARLY,
    FREE,
    PER,
    YEAR,
    MONTH,
    MOST_POPULAR,
    CURRENT_PLAN,
    ACTIVE_PLAN,
    GET_STARTED,
    UPGRADE_PLAN,
    UPCOMING_PLAN,
    LOADING_SUBSCRIPTION_PLANS,
    CONFIRM_YOUR_PLAN,
    START_YOUR_FREE_TRIAL_OF_PLAN,
    ADD_PAYMENT_METHOD_TO_START_SUBSCRIPTION,
    ADD_PAYMENT_METHOD_LINK,
};

const AllPlans = () => {
    const queryClient = useQueryClient();
    const navigate = useNavigate();
    const translatedText = useTranslation(languageTexts, 'none');
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
    const [plans, setPlans] = useState<SubscriptionPlan[]>([]);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(null);
    const [hasSubscription, setHasSubscription] = useState(false);
    const [isTrialEligible, setIsTrialEligible] = useState(false);
    const [hasPaymentMethod, setHasPaymentMethod] = useState(true);
    const [activePriceId, setActivePriceId] = useState<string | null>(null);
    const [upcomingPriceId, setUpcomingPriceId] = useState<string | null>(null);
    const [markedAsCanceled, setMarkedAsCanceled] = useState(false);
    const [isSubmittingPlan, setIsSubmittingPlan] = useState(false);
    const [actionErrorMessage, setActionErrorMessage] = useState('');
    const [toastMessage, setToastMessage] = useState<_ToastMessageProps>(defaultToastData);
    const { width: screenWidth } = useWindowSize();

    // Fetch organization subscription info
    const orgSubscriptionInfoQuery = useQuery({
        queryKey: ORG_SUBSCRIPTION_INFO,
        queryFn: () => getOrgSubscriptionInfo(queryClient),
        onSuccess: (response) => {
            const subscriptionInfo = response?.data?.data;
            if (subscriptionInfo) {
                setHasSubscription(subscriptionInfo?.hasSubscription || false);
                setIsTrialEligible(subscriptionInfo?.isTrialEligible || false);
                setHasPaymentMethod(subscriptionInfo?.paymentDetails?.hasPaymentMethod !== false);
            }
        },
        onError: (error) => {
            logger.log('Failed to fetch organization subscription info', error);
            setHasSubscription(false);
            setIsTrialEligible(false);
            setHasPaymentMethod(true);
        },
        refetchOnWindowFocus: false,
        enabled: true,
    });

    // Fetch organization subscription details to get active price ID
    const orgSubscriptionDetailsQuery = useQuery({
        queryKey: ORG_SUBSCRIPTION_DETAILS,
        queryFn: () => getOrgSubscriptionDetails(queryClient),
        onSuccess: (response) => {
            const apiData = response?.data?.data;
            const currentPriceId = apiData?.currentItems?.[0]?.priceId || apiData?.organizationProductPlan?.price?.priceId || null;
            const nextPeriodPriceId = apiData?.nextPeriodItems?.[0]?.priceId || null;

            setActivePriceId(currentPriceId);
            setUpcomingPriceId(currentPriceId && nextPeriodPriceId && currentPriceId !== nextPeriodPriceId ? nextPeriodPriceId : null);
            setMarkedAsCanceled(apiData?.markedAsCanceled === true);
        },
        onError: (error) => {
            logger.log('Failed to fetch organization subscription details', error);
            setActivePriceId(null);
            setUpcomingPriceId(null);
        },
        refetchOnWindowFocus: false,
        enabled: hasSubscription,
    });

    // Fetch organization subscription plans from API
    const orgSubscriptionPlansQuery = useQuery({
        queryKey: ORG_SUBSCRIPTION_PLANS_LIST,
        queryFn: () => getOrgSubscriptionPlans(queryClient),
        onSuccess: (response) => {
            const apiPlans = response?.data?.data;
            if (apiPlans && Array.isArray(apiPlans)) {
                const transformedPlans = transformApiPlansToComponentFormat(apiPlans);
                if (transformedPlans.length > 0) {
                    setPlans(transformedPlans);
                }
            }
        },
        onError: (error) => {
            logger.log('Failed to fetch organization subscription plans, using mock data', error);
            // Keep using mock data on error
        },
        refetchOnWindowFocus: false,
        enabled: true, // Auto-fetch on mount
    });

    const handleStartTrial = async () => {
        if (!selectedPlan || isSubmittingPlan) {
            return;
        }

        setActionErrorMessage('');
        setIsSubmittingPlan(true);
        try {
            // Determine priceId based on billing cycle
            const priceId = billingCycle === 'yearly' ? selectedPlan.yearlyPriceId : selectedPlan.monthlyPriceId;

            if (!priceId) {
                logger.log('Error: Price ID not found', { plan: selectedPlan, billingCycle });
                setToastMessage({
                    show: true,
                    message: 'Price ID not found for selected billing cycle',
                    variant: AlertVariant.ERROR,
                });
                return;
            }

            const response = await createOrgSubscription(queryClient, selectedPlan.planId, priceId);

            // Redirect to checkout URL from API response
            const checkoutUrl = response?.data?.data?.checkout_url;
            if (checkoutUrl) {
                window.location.href = checkoutUrl;
            } else {
                logger.log('Warning: No checkout URL in response', response);
                setToastMessage({
                    show: true,
                    message: 'Subscription created but checkout URL not found',
                    variant: AlertVariant.ERROR,
                });
            }
        } catch (error: any) {
            logger.log('Error creating subscription', error);
            setToastMessage({
                show: true,
                message: error?.response?.data?.message || 'Failed to create subscription. Please try again.',
                variant: AlertVariant.ERROR,
            });
        } finally {
            setIsSubmittingPlan(false);
        }
    };

    const handleStartSubscription = async () => {
        if (!selectedPlan || isSubmittingPlan) {
            return;
        }

        if (!hasPaymentMethod) {
            setActionErrorMessage(translatedText.ADD_PAYMENT_METHOD_TO_START_SUBSCRIPTION || ADD_PAYMENT_METHOD_TO_START_SUBSCRIPTION);
            return;
        }

        setActionErrorMessage('');
        setIsSubmittingPlan(true);
        try {
            // Determine priceId based on billing cycle
            const priceId = billingCycle === 'yearly' ? selectedPlan.yearlyPriceId : selectedPlan.monthlyPriceId;

            if (!priceId) {
                logger.log('Error: Price ID not found', { plan: selectedPlan, billingCycle });
                setToastMessage({
                    show: true,
                    message: 'Price ID not found for selected billing cycle',
                    variant: AlertVariant.ERROR,
                });
                return;
            }

            if (hasSubscription) {
                const response = await changeOrgSubscription(queryClient, selectedPlan.planId, priceId);
                await queryClient.invalidateQueries(ORG_SUBSCRIPTION_DETAILS);
                setToastMessage({
                    show: true,
                    message: response?.data?.message || 'Your subscription has been updated successfully.',
                    variant: AlertVariant.SUCCESS,
                });
                setDialogVisible(false);
                setSelectedPlan(null);
                window.setTimeout(() => {
                    navigate(`${routes.orgSubscriptions}?tab=overview`);
                }, 1200);
            } else {
                const response = await createOrgSubscription(queryClient, selectedPlan.planId, priceId);

                // Redirect to checkout URL from API response
                const checkoutUrl = response?.data?.data?.checkout_url;
                if (checkoutUrl) {
                    window.location.href = checkoutUrl;
                } else {
                    logger.log('Warning: No checkout URL in response', response);
                    setToastMessage({
                        show: true,
                        message: 'Subscription created but checkout URL not found',
                        variant: AlertVariant.ERROR,
                    });
                }
            }
        } catch (error: any) {
            logger.log('Error creating subscription', error);
            setToastMessage({
                show: true,
                message: error?.response?.data?.message || 'Failed to create subscription. Please try again.',
                variant: AlertVariant.ERROR,
            });
        } finally {
            setIsSubmittingPlan(false);
        }
    };

    const handleAddPaymentMethodClick = () => {
        setDialogVisible(false);
        setSelectedPlan(null);
        setActionErrorMessage('');
        navigate(`${routes.orgSubscriptions}?tab=overview`);
    };

    const renderPlanCard = (plan: SubscriptionPlan) => {
        // Use actual price from API based on billing cycle
        const displayPrice = billingCycle === 'yearly' ? plan.yearlyPrice || 0 : plan.monthlyPrice || plan.price || 0;
        const periodText = billingCycle === 'yearly' ? translatedText.YEAR : translatedText.MONTH;
        const selectedBillingPriceId = billingCycle === 'yearly' ? plan.yearlyPriceId : plan.monthlyPriceId;
        const isActivePlan = hasSubscription && selectedBillingPriceId === activePriceId;
        const isUpcomingPlan =
            hasSubscription && !!upcomingPriceId && selectedBillingPriceId === upcomingPriceId && activePriceId !== upcomingPriceId;

        return (
            <div className={`plan-card ${plan.isMostPopular ? 'popular' : ''}`}>
                {plan.isMostPopular && <div className="popular-badge">{translatedText.MOST_POPULAR}</div>}

                <div className="plan-icon">
                    <i className={plan.icon} />
                </div>

                <h3 className="plan-title">{plan.name}</h3>
                <p className="plan-description">{plan.description}</p>

                <div className="plan-pricing">
                    {displayPrice > 0 ? (
                        <>
                            <span className="price-currency">$</span>
                            <span className="price-amount">{displayPrice}</span>
                            <span className="price-period">
                                {translatedText.PER} {periodText}
                            </span>
                        </>
                    ) : (
                        <span className="price-free">{translatedText.FREE}</span>
                    )}
                </div>

                <ul className="plan-features">
                    {plan.features
                        ?.filter((f: string) => f)
                        .map((feature: string, index: number) => (
                            <li key={index}>
                                <i className="ri-checkbox-blank-circle-fill" style={{ fontSize: '8px' }} />
                                <span>{feature}</span>
                            </li>
                        ))}
                </ul>

                {isActivePlan ? (
                    <div className="current-plan-badge">
                        <i className="ri-check-line" />
                        <span>{translatedText.ACTIVE_PLAN}</span>
                    </div>
                ) : isUpcomingPlan ? (
                    <div className="current-plan-badge">
                        <i className="ri-calendar-schedule-line" />
                        <span>{translatedText.UPCOMING_PLAN}</span>
                    </div>
                ) : (
                    <Button
                        label={
                            markedAsCanceled
                                ? translatedText.GET_STARTED
                                : hasSubscription
                                  ? translatedText.UPGRADE_PLAN
                                  : translatedText.GET_STARTED
                        }
                        onClick={() => {
                            setSelectedPlan(plan);
                            setActionErrorMessage('');
                            setDialogVisible(true);
                        }}
                        icon="ri-arrow-right-line"
                        iconPos="right"
                        className="get-started-btn"
                        disabled={markedAsCanceled}
                    />
                )}
            </div>
        );
    };

    return (
        <div className="all-plans-container">
            <MlxToast
                message={toastMessage.message}
                onClose={() => setToastMessage(defaultToastData)}
                show={toastMessage.show}
                variant={toastMessage.variant}
            />
            {/* Header */}
            <div className="plans-header">
                <h1>{translatedText.CHOOSE_YOUR_PLAN}</h1>
                <p>{translatedText.SUBSCRIPTION_PLANS_DESCRIPTION}</p>
            </div>

            {/* Confirmation Modal */}
            <MlxDialog
                visible={dialogVisible}
                onHide={() => {
                    setDialogVisible(false);
                    setSelectedPlan(null);
                    setActionErrorMessage('');
                }}
                header={translatedText.CONFIRM_YOUR_PLAN}
                style={{ width: screenWidth <= MOBILE_BREAKPOINT ? '100%' : '60%' }}
                // style={{ minWidth: '45rem' }}
            >
                <PlanConfirmationModal
                    selectedPlan={selectedPlan}
                    billingCycle={billingCycle}
                    onStartTrial={handleStartTrial}
                    onStartSubscription={handleStartSubscription}
                    hasSubscription={hasSubscription}
                    isTrialEligible={isTrialEligible}
                    activePriceId={activePriceId}
                    isSubmitting={isSubmittingPlan}
                    actionErrorMessage={actionErrorMessage}
                    onAddPaymentMethodClick={handleAddPaymentMethodClick}
                />
            </MlxDialog>

            {/* Billing Toggle */}
            <div className="billing-toggle">
                <span className={billingCycle === 'monthly' ? 'active' : ''}>{translatedText.MONTHLY}</span>
                <button className="toggle-switch" onClick={() => setBillingCycle(billingCycle === 'monthly' ? 'yearly' : 'monthly')}>
                    <span className={`toggle-slider ${billingCycle}`} />
                </button>
                <span className={billingCycle === 'yearly' ? 'active' : ''}>{translatedText.YEARLY}</span>
            </div>

            {/* Plans Grid */}
            <MlxBlockUI
                isLoading={
                    orgSubscriptionPlansQuery.isLoading ||
                    orgSubscriptionPlansQuery.isFetching ||
                    orgSubscriptionInfoQuery.isLoading ||
                    orgSubscriptionDetailsQuery.isLoading
                }
            >
                <div className="plans-grid">
                    {plans
                        .filter((plan) => {
                            const priceId = billingCycle === 'yearly' ? plan.yearlyPriceId : plan.monthlyPriceId;
                            return !!priceId;
                        })
                        .map((plan) => (
                            <div key={plan.id}>{renderPlanCard(plan)}</div>
                        ))}
                </div>
            </MlxBlockUI>
        </div>
    );
};

export default AllPlans;
